import {
  Alert,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Stepper,
  Switch,
  Table,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { PiCheck, PiPaperPlaneTilt, PiWarning } from 'react-icons/pi';
import { PayerPicker } from '@/components/InvoiceDetailsModal/PayerPicker';
import { formatDate } from '@/lib/helpers';
import { holds } from '@/lib/permissionMatch';
import { proformaStateLabel } from '@/lib/proformaState';
import {
  billedRateLines,
  type TAmendedInvoice,
  type TAmendIssuerOption,
  type TAmendRequest,
  type TAmendResult,
  type TDocumentFx,
  type TPlanImpactRow,
  type TReportedPayment,
  useAmendInvoice,
  useAmendOptions,
  useSendInvoice,
} from '@/models/invoices';
import { usePermissions } from '@/models/permissions';
import type { TInvoice } from '@/types';

const money = (amount: number, currency: string) =>
  `${currency} ${amount.toFixed(2)}`;

const pct = (rate: number) =>
  `${(rate * 100).toFixed(2).replace(/\.00$/, '')}%`;

/** "1 USD = 0.8669 EUR (19.10.2026)", or "-" for a document in one currency. */
const fxText = (fx: TDocumentFx | null | undefined) =>
  billedRateLines(fx).join('; ') || '-';

interface Props {
  invoice: TInvoice;
  opened: boolean;
  onClose: () => void;
}

export function AmendInvoiceModal({ invoice, opened, onClose }: Props) {
  const invoiceId = Number(invoice.id);

  const [step, setStep] = useState(0);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [issuerId, setIssuerId] = useState<number | null>(null);
  const [vatOn, setVatOn] = useState(false);
  const [clearVat, setClearVat] = useState(false);
  const [revertVat, setRevertVat] = useState(false);
  const [vatRate, setVatRate] = useState<number | null>(null);
  const [vatReason, setVatReason] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<TAmendResult | null>(null);
  const [committed, setCommitted] = useState<TAmendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Eligibility follows what the accountant is currently considering, not what
  // the invoice happens to hold - changing the payer moves which companies may
  // legally issue, and the list must say so before they submit.
  const options = useAmendOptions(
    invoiceId,
    {
      payerId: payerId ?? undefined,
      paymentMethod: paymentMethod ?? undefined,
      currency: currency ?? undefined,
      invoicingCompanyId: issuerId ?? undefined,
    },
    opened,
  );
  const amend = useAmendInvoice(invoice.offerId ?? undefined);
  const send = useSendInvoice();
  const maySend = holds(usePermissions().permissions, 'invoices:send');

  const data = options.data;
  const effectivePayerId = payerId ?? data?.current.payerId ?? null;
  const effectiveMethod = paymentMethod ?? data?.current.paymentMethod ?? '';
  const effectiveIssuerId =
    issuerId ?? data?.current.invoicingCompanyId ?? null;
  const isCrypto = effectiveMethod === 'Cryptocurrency';

  const selectedIssuer = data?.issuers.find((i) => i.id === effectiveIssuerId);

  // The terms the amendment changes, stated before and after on the review step.
  const terms: TermChange[] = (() => {
    if (!data) return [];
    const payerName = (id: number | null) =>
      id == null
        ? '-'
        : (data.payers.find((p) => p.id === id)?.name ?? `#${id}`);
    const issuerName = (id: number | null) => {
      const c = data.issuers.find((i) => i.id === id);
      return c ? `${c.name} (${c.countryCode})` : id == null ? '-' : `#${id}`;
    };
    const currencyAfter = isCrypto
      ? 'USD'
      : (currency ?? data.current.bankCurrency ?? data.current.currency);
    const rows: TermChange[] = [
      {
        key: 'payer',
        label: 'Payer',
        before: payerName(data.current.payerId),
        after: payerName(effectivePayerId),
      },
      {
        key: 'method',
        label: 'Payment method',
        before: data.current.paymentMethod,
        after: effectiveMethod,
      },
      {
        key: 'currency',
        label: 'Currency',
        before: data.current.currency.toUpperCase(),
        after: String(currencyAfter).toUpperCase(),
      },
      {
        key: 'issuer',
        label: 'Issuer',
        before: issuerName(data.current.invoicingCompanyId),
        after: issuerName(effectiveIssuerId),
      },
    ];
    return rows.filter((r) => r.before !== r.after);
  })();
  // A company with no rail cannot be paid, so the invoice cannot say where to
  // pay - unlike a self-dealing bar, no permission and no reason lifts it. It
  // reaches this screen only when the currency or method just moved away from
  // what the invoice's own issuer holds.
  const issuerUnpayable = selectedIssuer?.blockRule === 'RAIL';
  // An issuer that levies no VAT has no rate to state or override. Its
  // invoices print no rate at all, so the whole VAT section is meaningless -
  // except where a manual rate stands, which has to be cleared deliberately.
  const issuerLeviesVat = selectedIssuer?.vatApplicable !== false;
  const mustClearVat = !issuerLeviesVat && data?.current.vatOverridden === true;
  const issuerBarred = selectedIssuer
    ? !selectedIssuer.eligible && !issuerUnpayable
    : false;

  // Bank transfer on an invoice that went through crypto has no remembered
  // currency to fall back on, so it has to be chosen explicitly.
  const mustChooseCurrency =
    !isCrypto && data?.requiresExplicitCurrency && !currency;

  const request = useMemo<TAmendRequest>(() => {
    const body: TAmendRequest = { id: invoiceId };
    if (payerId != null) body.payerId = payerId;
    if (paymentMethod) body.paymentMethod = paymentMethod;
    if (currency && !isCrypto) body.currency = currency;
    if (issuerId != null) body.invoicingCompanyId = issuerId;
    if (issuerBarred) body.overrideSelfDealing = true;
    // A rate left over from an earlier issuer choice must not be sent to one
    // that levies none: the amendment would be refused, and the accountant
    // would be told about a control the form no longer shows.
    if (vatOn && issuerLeviesVat) {
      body.vat = {
        rate: vatRate ?? 0,
        reason: vatReason,
      };
    }
    if (revertVat && !vatOn) body.revertVatToAutomatic = true;
    if (clearVat) body.clearVatOverride = true;
    if (reason) body.reason = reason;
    return body;
  }, [
    invoiceId,
    payerId,
    paymentMethod,
    currency,
    isCrypto,
    issuerId,
    issuerBarred,
    vatOn,
    vatRate,
    vatReason,
    revertVat,
    clearVat,
    issuerLeviesVat,
    reason,
  ]);

  const changedSomething = Object.keys(request).length > 1;

  const blockingIssue = (() => {
    if (!data) return 'Loading';
    if (!data.amendable) {
      return data.status === 'issued'
        ? 'Money has been collected against this proforma: only an Open proforma with nothing collected can be amended'
        : `Only an Open proforma with nothing collected can be amended; this one is ${proformaStateLabel(data.status)}`;
    }
    if (!changedSomething) return 'Change something first';
    if (mustChooseCurrency) return 'Choose the bank transfer currency';
    if (issuerUnpayable) {
      return (
        selectedIssuer?.blockText ??
        'This issuing company cannot be paid this way'
      );
    }
    if (issuerBarred && !data.mayOverrideSelfDealing) {
      return 'You may not issue from a company barred by the self-dealing rules';
    }
    if (issuerBarred && !reason.trim()) {
      return 'A reason is required to override self-dealing prevention';
    }
    if (mustClearVat && !clearVat) {
      return 'Clear the manual VAT rate to use this issuing company';
    }
    // The VAT editor is not shown for an issuer that levies none, so a rate
    // left in state from an earlier choice is not sent and not validated.
    if (issuerLeviesVat && vatOn && vatRate == null)
      return 'Enter the VAT rate';
    if (issuerLeviesVat && vatOn && !vatReason.trim()) {
      return 'A VAT override needs a reason';
    }
    return null;
  })();

  const runPreview = async () => {
    setError(null);
    try {
      const result = await amend.mutateAsync({ ...request, dryRun: true });
      setPreview(result);
      setStep(1);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const commit = async () => {
    setError(null);
    try {
      const result = await amend.mutateAsync(request);
      setCommitted(result);
      setStep(2);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const close = () => {
    setStep(0);
    setPreview(null);
    setCommitted(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      size="xl"
      title={`Amend invoice ${invoice.invoiceNumber ?? ''}`}
    >
      <Stepper active={step} size="sm" mb="md">
        <Stepper.Step label="Change" />
        <Stepper.Step label="Review impact" />
        <Stepper.Step label="Done" />
      </Stepper>

      {options.isLoading && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}

      {error && (
        <Alert color="red" icon={<PiWarning />} mb="md">
          {error}
        </Alert>
      )}

      <Box pos="relative">
        <LoadingOverlay
          visible={options.isFetching && !options.isLoading}
          zIndex={100}
          overlayProps={{ blur: 0, backgroundOpacity: 0.35 }}
          loaderProps={{ size: 'sm' }}
        />

        {step === 0 && data && (
          <Stack gap="md">
            {data.reportedPayments.length > 0 && (
              <ReportedPaymentWarning reports={data.reportedPayments} />
            )}

            {data.current.partNumber != null && (
              <Alert color="blue">
                This is installment {data.current.partNumber}. The change
                applies to this installment and every later one that has
                received no money; installments partly or fully paid are left
                untouched and listed on the next step.
              </Alert>
            )}

            <PayerPicker
              label="Bill to"
              description="Drives the VAT treatment and which companies may issue"
              value={effectivePayerId}
              onChange={setPayerId}
              options={data.payers.map((p) => ({
                id: p.id,
                name: p.name,
                countryCode: p.countryCode,
                clientType: p.clientType,
                selectable: p.eligible,
                note: p.eligible ? null : p.blockText,
              }))}
            />

            <Group grow align="flex-start">
              <Select
                label="Payment method"
                value={effectiveMethod}
                onChange={(v) => {
                  setPaymentMethod(v);
                  if (v === 'Cryptocurrency') setCurrency(null);
                }}
                data={['Bank Transfer', 'Cryptocurrency']}
              />
              <Select
                label="Currency"
                description={
                  isCrypto
                    ? 'Cryptocurrency invoices are always USD'
                    : mustChooseCurrency
                      ? 'This invoice was issued in crypto, so it does not remember a bank currency'
                      : undefined
                }
                disabled={isCrypto}
                error={mustChooseCurrency ? 'Required' : undefined}
                value={
                  isCrypto
                    ? 'USD'
                    : (currency ?? data.current.bankCurrency ?? null)
                }
                onChange={setCurrency}
                data={['EUR', 'USD', 'GBP']}
              />
            </Group>

            <IssuerPicker
              issuers={data.issuers}
              value={effectiveIssuerId}
              onChange={setIssuerId}
              mayOverride={data.mayOverrideSelfDealing}
            />

            {issuerUnpayable && (
              <Alert color="red" icon={<PiWarning />}>
                <Text size="sm" fw={600}>
                  {selectedIssuer?.name} cannot be paid this way.
                </Text>
                <Text size="sm">{selectedIssuer?.blockText}.</Text>
              </Alert>
            )}

            {issuerBarred && (
              <Alert color="red" icon={<PiWarning />}>
                <Text size="sm" fw={600}>
                  This overrides self-dealing prevention (
                  {selectedIssuer?.blockRule}).
                </Text>
                <Text size="sm">{selectedIssuer?.blockText}.</Text>
              </Alert>
            )}

            <Divider label="VAT" />

            {!issuerLeviesVat ? (
              <Alert color="grape" variant="light">
                <Text size="sm">
                  {selectedIssuer?.name} levies no VAT. Its invoices state no
                  rate at all, so there is nothing to set here.
                </Text>
                {mustClearVat && (
                  <>
                    <Text size="sm" mt="xs">
                      This invoice carries a manually set VAT rate, which this
                      issuer cannot carry. Confirm clearing it to continue.
                    </Text>
                    <Switch
                      mt="xs"
                      size="sm"
                      label="Clear the manual VAT rate"
                      checked={clearVat}
                      onChange={(e) => setClearVat(e.currentTarget.checked)}
                    />
                  </>
                )}
              </Alert>
            ) : (
              <>
                <VatComparison
                  automaticRate={data.automaticVat?.vatRate ?? null}
                  automaticNote={data.automaticVat?.vatNote ?? null}
                  derivation={
                    data.automaticVat
                      ? `${data.automaticVat.buyerType.toLowerCase()} buyer in ${data.automaticVat.buyerCountry}, billed from ${data.automaticVat.companyCountry}`
                      : null
                  }
                  manualRate={
                    vatOn
                      ? vatRate
                      : revertVat
                        ? null
                        : data.current.vatOverridden
                          ? data.current.vatRate
                          : null
                  }
                />

                {data.current.vatOverridden && !vatOn && (
                  <Alert color="yellow">
                    <Text size="sm">
                      VAT is set manually. It stays as it is when the payer or
                      issuing company changes, until somebody amends it again.
                    </Text>
                    {data.current.vatOverrideReason && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Reason: {data.current.vatOverrideReason}
                      </Text>
                    )}
                    <Switch
                      mt="xs"
                      size="sm"
                      label="Revert to the automatic VAT"
                      checked={revertVat}
                      onChange={(e) => setRevertVat(e.currentTarget.checked)}
                    />
                  </Alert>
                )}

                <Switch
                  label={
                    data.current.vatOverridden
                      ? 'Set a different VAT rate'
                      : 'Set VAT manually'
                  }
                  checked={vatOn}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setVatOn(on);
                    // Start from the rate actually in force, so opening the editor
                    // cannot make a standing override look like it vanished.
                    if (on && vatRate == null && data.current.vatOverridden) {
                      setVatRate(data.current.vatRate);
                    }
                  }}
                />

                {vatOn && (
                  <Stack gap="xs" pl="md">
                    <NumberInput
                      label="VAT rate"
                      suffix=" %"
                      min={0}
                      max={100}
                      decimalScale={2}
                      required
                      value={vatRate == null ? '' : vatRate * 100}
                      onChange={(v) =>
                        setVatRate(v === '' ? null : Number(v) / 100)
                      }
                    />

                    <Textarea
                      label="Reason"
                      description="Recorded in the audit log, never printed"
                      required
                      autosize
                      minRows={2}
                      value={vatReason}
                      onChange={(e) => setVatReason(e.currentTarget.value)}
                    />
                  </Stack>
                )}
              </>
            )}

            <Divider />

            <Textarea
              label="Reason for this amendment"
              description={
                issuerBarred
                  ? 'Required when overriding self-dealing prevention'
                  : 'Optional, recorded in the audit log'
              }
              required={issuerBarred}
              autosize
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Tooltip label={blockingIssue} disabled={!blockingIssue}>
                <Button
                  onClick={runPreview}
                  loading={amend.isPending}
                  disabled={!!blockingIssue}
                >
                  Review impact
                </Button>
              </Tooltip>
            </Group>
          </Stack>
        )}

        {step === 1 && preview && (
          <Stack gap="md">
            <ImpactTable result={preview} invoice={invoice} terms={terms} />

            {preview.amended
              .filter((a) => a.reportedPayments.length > 0)
              .map((a) => (
                <ReportedPaymentWarning
                  key={a.id}
                  reports={a.reportedPayments}
                  invoiceNumber={a.invoiceNumber}
                />
              ))}

            {preview.skipped.length > 0 && (
              <Alert color="yellow" icon={<PiWarning />}>
                {preview.skipped.length === 1
                  ? 'One later installment cannot be amended and is left unchanged. Handle it separately.'
                  : `${preview.skipped.length} later installments cannot be amended and are left unchanged. Handle them separately.`}
              </Alert>
            )}

            <Alert color="gray">
              Nothing is sent to the client. After amending you can download the
              PDF, check it, and send it yourself.
            </Alert>

            <Group justify="space-between">
              <Button variant="default" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button onClick={commit} loading={amend.isPending} color="orange">
                Amend{' '}
                {preview.amended.length > 1
                  ? `${preview.amended.length} invoices`
                  : 'invoice'}
              </Button>
            </Group>
          </Stack>
        )}

        {step === 2 && committed && (
          <Stack gap="md">
            <Alert color="green" icon={<PiCheck />}>
              {committed.amended.length > 1
                ? `${committed.amended.length} invoices are now at a new version and have not been sent.`
                : 'The invoice is now at a new version and has not been sent.'}
            </Alert>

            <Table withTableBorder>
              <Table.Tbody>
                {committed.amended.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>{a.invoiceNumber}</Table.Td>
                    <Table.Td>
                      <Badge variant="light">v{a.documentVersion}</Badge>
                    </Table.Td>
                    <Table.Td>{money(a.payableAmount, a.currency)}</Table.Td>
                    <Table.Td>
                      <Badge color="orange" variant="light">
                        not sent
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {maySend && (
                        <Button
                          size="compact-xs"
                          variant="default"
                          leftSection={<PiPaperPlaneTilt size={12} />}
                          loading={send.isPending}
                          onClick={() => send.mutate({ id: a.id })}
                        >
                          Send
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Close
              </Button>
              {maySend && committed.amended.length > 1 && (
                <Button
                  leftSection={<PiPaperPlaneTilt size={14} />}
                  loading={send.isPending}
                  onClick={async () => {
                    for (const a of committed.amended) {
                      await send.mutateAsync({ id: a.id });
                    }
                    close();
                  }}
                >
                  Send all to client
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Box>
    </Modal>
  );
}

/**
 * Both answers, side by side: what the platform derives and what the accountant
 * has stated. Showing only the winner hides the fact that a manual rate is a
 * departure, which is precisely the thing a reviewer needs to see.
 */
function VatComparison({
  automaticRate,
  automaticNote,
  derivation,
  manualRate,
}: {
  automaticRate: number | null;
  automaticNote: string | null;
  derivation: string | null;
  manualRate: number | null;
}) {
  const overridden = manualRate != null;
  return (
    <Table withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>VAT</Table.Th>
          <Table.Th>Rate</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>
            <Text size="sm" c={overridden ? 'dimmed' : undefined}>
              Matrix
            </Text>
            {derivation && (
              <Text size="xs" c="dimmed">
                {derivation}
              </Text>
            )}
            {automaticNote && (
              <Text size="xs" c="dimmed" fs="italic">
                {automaticNote}
              </Text>
            )}
          </Table.Td>
          <Table.Td>
            <Text
              size="sm"
              fw={overridden ? 400 : 700}
              c={overridden ? 'dimmed' : undefined}
              td={overridden ? 'line-through' : undefined}
            >
              {automaticRate == null ? '—' : pct(automaticRate)}
            </Text>
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td>
            <Group gap={6}>
              <Text size="sm">Manual</Text>
              {overridden && (
                <Badge size="xs" color="yellow" variant="light">
                  applies
                </Badge>
              )}
            </Group>
          </Table.Td>
          <Table.Td>
            <Text
              size="sm"
              fw={overridden ? 700 : 400}
              c={overridden ? undefined : 'dimmed'}
            >
              {overridden ? pct(manualRate) : 'not set'}
            </Text>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
}

function IssuerPicker({
  issuers,
  value,
  onChange,
  mayOverride,
}: {
  issuers: TAmendIssuerOption[];
  value: number | null;
  onChange: (id: number) => void;
  mayOverride: boolean;
}) {
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        Issuing company
      </Text>
      <Radio.Group
        value={value ? String(value) : null}
        onChange={(v) => onChange(Number(v))}
      >
        <Stack gap={4}>
          {issuers.map((c) => {
            // Barred companies stay visible with their reason: hiding them
            // turns "why can't I pick Panama" into a support question.
            // Self-dealing bars yield to the override permission; a missing
            // payment rail does not, because no permission conjures a bank
            // account into existence.
            const selectable =
              c.eligible || (mayOverride && c.blockRule !== 'RAIL');
            return (
              <Tooltip
                key={c.id}
                disabled={c.eligible}
                label={
                  c.blockRule === 'RAIL'
                    ? c.blockText
                    : `${c.blockRule}: ${c.blockText}`
                }
                multiline
                w={320}
                withArrow
                position="top-start"
                fz="xs"
              >
                <div>
                  <Radio
                    value={String(c.id)}
                    disabled={!selectable}
                    label={
                      <Group gap="xs">
                        <Text size="sm">
                          {c.name} ({c.countryCode})
                        </Text>
                        {c.vatRate != null && (
                          <Badge size="xs" variant="light" color="blue">
                            VAT {pct(c.vatRate)}
                          </Badge>
                        )}
                        {c.eligible && (
                          <Text size="xs" c="dimmed">
                            priority {c.priority}
                          </Text>
                        )}
                      </Group>
                    }
                  />
                </div>
              </Tooltip>
            );
          })}
        </Stack>
      </Radio.Group>
    </Stack>
  );
}

/** A term of the proforma the amendment changes: who pays, how, in what, issued by whom. */
export type TermChange = {
  key: string;
  label: string;
  before: string;
  after: string;
};

function ImpactTable({
  result,
  invoice,
  terms,
}: {
  result: TAmendResult;
  invoice: TInvoice;
  terms: TermChange[];
}) {
  const before = {
    subtotal: Number(invoice.subtotalAmount ?? 0),
    vatAmount: Number(invoice.vatAmount ?? 0),
    total: Number(invoice.totalAmount ?? 0),
    // Compare like with like: what the client owes now against what they will
    // owe, both in the document currency. `dueAmount` on the row is the
    // invoice as issued, before any discount, and is not that figure.
    due: Number(invoice.money?.payable ?? invoice.totalAmount ?? 0),
    currency: String(invoice.baseCurrency ?? invoice.dueCurrency ?? 'EUR'),
  };
  const target = result.amended.find(
    (a) => Number(a.id) === Number(invoice.id),
  );

  return (
    <Stack gap="sm">
      {target && (
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>Before</Table.Th>
              <Table.Th>After</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {terms.map((t) => (
              <MoneyRow
                key={t.key}
                label={t.label}
                before={t.before}
                after={t.after}
              />
            ))}
            <MoneyRow
              label="Subtotal"
              before={money(before.subtotal, before.currency)}
              after={money(target.subtotalAmount, target.currency)}
            />
            <MoneyRow
              label={`VAT (${pct(target.vatRate)})`}
              before={money(before.vatAmount, before.currency)}
              after={money(target.vatAmount, target.currency)}
            />
            <MoneyRow
              label="Total"
              before={money(before.total, before.currency)}
              after={money(target.totalAmount, target.currency)}
            />
            {(before.total - before.due > 0.005 ||
              target.totalAmount - target.payableAmount > 0.005) && (
              <MoneyRow
                label={`Discount${
                  before.total > 0
                    ? ` (${Number((((before.total - before.due) / before.total) * 100).toFixed(2))}%)`
                    : ''
                }`}
                before={`-${money(before.total - before.due, before.currency)}`}
                after={`-${money(target.totalAmount - target.payableAmount, target.currency)}`}
              />
            )}
            <MoneyRow
              label="Due"
              before={money(before.due, before.currency)}
              after={money(target.payableAmount, target.currency)}
            />
            {target.fxBefore?.rates.length || target.fxAfter?.rates.length ? (
              <MoneyRow
                label="Rate"
                before={fxText(target.fxBefore)}
                after={fxText(target.fxAfter)}
              />
            ) : null}
          </Table.Tbody>
        </Table>
      )}

      {result.amended
        .filter((a) => a.fxWarning)
        .map((a) => (
          <Alert key={a.id} color="orange" icon={<PiWarning />}>
            <Text size="sm">
              {a.invoiceNumber}: {a.fxWarning}
            </Text>
          </Alert>
        ))}

      {(result.planImpact?.length ?? 0) > 0 && (
        <PlanImpactTable
          rows={result.planImpact!}
          takes={terms.map((t) => t.label.toLowerCase())}
        />
      )}

      {!result.planImpact?.length &&
        (result.amended.length > 1 ||
          result.skipped.length > 0 ||
          result.inheritedPlanItemIds.length > 0) && (
          <>
            <Text size="sm" fw={500}>
              Installments
            </Text>
            <Table withTableBorder>
              <Table.Tbody>
                {result.amended.map((a: TAmendedInvoice) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>{a.invoiceNumber}</Table.Td>
                    <Table.Td>{money(a.payableAmount, a.currency)}</Table.Td>
                    <Table.Td>
                      <Badge color="orange" variant="light">
                        amended
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {result.skipped.map((s) => (
                  <Table.Tr key={s.invoiceId}>
                    <Table.Td>Installment {s.seq}</Table.Td>
                    <Table.Td>
                      {proformaStateLabel(s.status)}
                      {s.reason ? ` - ${s.reason}` : ''}
                    </Table.Td>
                    <Table.Td>
                      <Badge color="gray" variant="light">
                        skipped - handle separately
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {result.inheritedPlanItemIds.length > 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      {result.inheritedPlanItemIds.length} installment(s) not
                      yet issued
                    </Table.Td>
                    <Table.Td>
                      <Badge color="blue" variant="light">
                        inherit the new values
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </>
        )}
    </Stack>
  );
}

/**
 * A payment confirmation was already provided for the document: each one, and the payment it is
 * for when one is associated, highlighted.
 */
export function ReportedPaymentWarning({
  reports,
  invoiceNumber,
  consequence = 'The confirmations stay on the invoice. They were provided against the document as it stands now: check them before sending the corrected one.',
}: {
  reports: TReportedPayment[];
  invoiceNumber?: string;
  consequence?: string;
}) {
  return (
    <Alert color="orange" icon={<PiWarning />}>
      <Text size="sm" fw={600}>
        {reports.length > 1
          ? `${reports.length} payment confirmations have`
          : 'A payment confirmation has'}{' '}
        already been provided for{' '}
        {invoiceNumber ? `invoice ${invoiceNumber}` : 'this invoice'}.
      </Text>
      {reports.map((report) => (
        <Text size="sm" key={report.id}>
          {report.reportedAt
            ? `${formatDate(report.reportedAt)}: `
            : 'Date unknown: '}
          {report.hash ? (
            <>
              transaction hash <Code>{report.hash}</Code>
            </>
          ) : (
            `file ${report.fileName ?? `#${report.fileId}`}`
          )}
          {report.payment && (
            <Text span inherit fw={700} c="teal.8">
              {' '}
              - for the payment of{' '}
              {money(report.payment.amount, report.payment.currency)} on{' '}
              {formatDate(report.payment.collectedAt)}
            </Text>
          )}
        </Text>
      ))}
      <Text size="sm">{consequence}</Text>
    </Alert>
  );
}

const EFFECT: Record<
  TPlanImpactRow['effect'],
  { label: string; color?: string }
> = {
  this: { label: 'This proforma', color: 'blue' },
  amended: { label: 'Amended with it', color: 'orange' },
  unchanged: { label: 'Unchanged', color: 'dimmed' },
  inherits: { label: 'Takes the new terms', color: 'teal' },
  'not issued': { label: 'Not issued, unchanged', color: 'dimmed' },
};

const impactMoney = (m: TPlanImpactRow['before']) =>
  m ? money(m.amount, m.currency) : '-';

/**
 * Every installment of the plan and what the amendment does to it: earlier ones that are paid
 * stay as they are and say why, this one and later unpaid ones are re-priced, and the ones not
 * issued yet take the new terms with the rate fixed now and the amount it would bill.
 */
function PlanImpactTable({
  rows,
  takes,
}: {
  rows: TPlanImpactRow[];
  /** The terms the amendment changes, which an installment not issued yet takes. */
  takes: string[];
}) {
  const why = (r: TPlanImpactRow) =>
    r.effect === 'this'
      ? ''
      : r.effect === 'inherits' && takes.length > 0
        ? `Not issued yet: takes the new ${takes.join(', ')}`
        : r.why;
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        Installments
      </Text>
      <Table withTableBorder fz="xs" verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={30}>#</Table.Th>
            <Table.Th>What happens</Table.Th>
            <Table.Th>Before</Table.Th>
            <Table.Th>After</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => {
            const moved =
              r.before != null &&
              r.after != null &&
              (r.before.amount !== r.after.amount ||
                r.before.currency !== r.after.currency);
            return (
              <Table.Tr
                key={r.seq}
                bg={
                  r.effect === 'this'
                    ? 'var(--mantine-color-blue-light)'
                    : undefined
                }
              >
                <Table.Td>{r.seq}</Table.Td>
                <Table.Td>
                  <Text fz="xs" fw={600} c={EFFECT[r.effect].color}>
                    {EFFECT[r.effect].label}
                    {r.invoiceNumber ? ` · ${r.invoiceNumber}` : ''}
                  </Text>
                  <Text fz={11} c="dimmed">
                    {[
                      why(r),
                      r.agreed
                        ? `agreed ${money(r.agreed.amount, r.agreed.currency)}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    fz="xs"
                    c="dimmed"
                    td={moved ? 'line-through' : undefined}
                  >
                    {impactMoney(r.before)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    fz="xs"
                    fw={moved || r.effect === 'inherits' ? 700 : 400}
                  >
                    {r.effect === 'inherits' && r.after ? '≈ ' : ''}
                    {impactMoney(r.after)}
                  </Text>
                  {r.rate && (
                    <Text fz={11} c="dimmed">
                      1 {r.rate.from} = {Number(r.rate.rate.toFixed(6))}{' '}
                      {r.rate.to}
                      {r.rate.on
                        ? ` (${r.effect === 'inherits' ? 'fixed ' : ''}${r.rate.on
                            .split('-')
                            .reverse()
                            .join('.')})`
                        : ''}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function MoneyRow({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  const moved = before !== after;
  return (
    <Table.Tr>
      <Table.Td>{label}</Table.Td>
      <Table.Td>
        <Text size="sm" td={moved ? 'line-through' : undefined} c="dimmed">
          {before}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={moved ? 700 : 400}>
          {after}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

function errorText(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    return String((e as { message: unknown }).message);
  }
  return 'The amendment could not be applied';
}

export default AmendInvoiceModal;
